import React, { createContext, useContext } from 'react'

import { useCallback, useState } from 'hooks'

const PERIODS_WITH_CUSTOM_RANGE = {
  WEEK: 'week',
  MONTH: 'month',
  SIX_MONTHS: 'six_months',
  YEAR: 'year',
  MAX: 'max',
  RANGE: 'range',
} as const

const NAME_BY_PERIOD = {
  [PERIODS_WITH_CUSTOM_RANGE.WEEK]: 'last week',
  [PERIODS_WITH_CUSTOM_RANGE.MONTH]: 'last month',
  [PERIODS_WITH_CUSTOM_RANGE.SIX_MONTHS]: 'last six months',
  [PERIODS_WITH_CUSTOM_RANGE.YEAR]: 'last year',
  [PERIODS_WITH_CUSTOM_RANGE.MAX]: 'max available period',
  [PERIODS_WITH_CUSTOM_RANGE.RANGE]: 'custom period',
}

type PERIOD = (typeof PERIODS_WITH_CUSTOM_RANGE)[keyof typeof PERIODS_WITH_CUSTOM_RANGE]

type PeriodWithCustomRangeContextType = {
  selectedPeriod: PERIOD
  previousPeriod: PERIOD | null
  handlePeriodSelect: (value: PERIOD) => void
  resetPreviousPeriod: () => void
}

const PeriodWithCustomRangeContext = createContext<PeriodWithCustomRangeContextType | null>(null)

const PeriodWithCustomRangeProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [selectedPeriod, setSelectedPeriod] = useState<PERIOD>(PERIODS_WITH_CUSTOM_RANGE.MAX)
  const [previousPeriod, setPreviousPeriod] = useState<PERIOD | null>(null)

  const handlePeriodSelect = useCallback(
    (value: PERIOD) => {
      setPreviousPeriod(selectedPeriod)
      setSelectedPeriod(value)
    },
    [selectedPeriod],
  )

  const resetPreviousPeriod = useCallback(() => {
    setPreviousPeriod(null)
  }, [])

  return (
    <PeriodWithCustomRangeContext.Provider
      value={{ selectedPeriod, previousPeriod, handlePeriodSelect, resetPreviousPeriod }}
    >
      {children}
    </PeriodWithCustomRangeContext.Provider>
  )
}

const usePeriodWithCustomRange = (): PeriodWithCustomRangeContextType => {
  const context = useContext(PeriodWithCustomRangeContext)

  if (!context) {
    throw new Error('usePeriodWithCustomRange must be used within a PeriodWithCustomRangeProvider')
  }
  return context
}

export {
  usePeriodWithCustomRange,
  PeriodWithCustomRangeProvider,
  PERIODS_WITH_CUSTOM_RANGE,
  NAME_BY_PERIOD,
  type PERIOD,
}
